import { useEffect, useMemo, useRef, useState } from "react";

function norm(s: string): string {
  return s
    .toLowerCase()
    .replace(/[’ʼ`´]/g, "'")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "");
}

interface Props {
  /** Suggestion pool: display name + optional secondary match key (en title). */
  names: { uk: string; en?: string }[];
  disabled: boolean;
  busy: boolean;
  exclude: Set<string>;
  onSubmit: (name: string) => void;
  placeholder?: string;
}

/** CountryInput's pattern, but the pool comes as a prop (people, film titles). */
export function SuggestInput({ names, disabled, busy, exclude, onSubmit, placeholder }: Props) {
  const [value, setValue] = useState("");
  const ref = useRef<HTMLInputElement>(null);
  const suggestions = useMemo(() => {
    const q = norm(value.trim());
    if (q.length < 2) return [];
    const pool = names.filter((n) => !exclude.has(n.uk));
    const starts = pool.filter((n) => norm(n.uk).startsWith(q) || (n.en && norm(n.en).startsWith(q)));
    const contains = pool.filter(
      (n) => !starts.includes(n) && (norm(n.uk).includes(q) || (n.en && norm(n.en).includes(q))),
    );
    return [...starts, ...contains].slice(0, 4);
  }, [value, names, exclude]);

  useEffect(() => {
    if (disabled) ref.current?.blur();
  }, [disabled]);

  const submit = (name: string) => {
    if (!name.trim() || disabled || busy) return;
    ref.current?.focus();
    onSubmit(name.trim());
    setValue("");
  };

  return (
    <div className="px-4 pb-1">
      <form
        className="flex gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          submit(suggestions[0]?.uk ?? value);
        }}
      >
        <input
          ref={ref}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          readOnly={disabled}
          placeholder={disabled ? "гра закінчена" : (placeholder ?? "…")}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="words"
          spellCheck={false}
          enterKeyHint="send"
          className="h-[44px] flex-1 rounded-xl border border-[rgba(128,128,128,0.35)] bg-transparent px-4 text-[16px] text-tgtext outline-none placeholder:text-[rgba(128,128,128,0.7)] focus:border-tgbutton disabled:opacity-50"
        />
        <button
          type="submit"
          disabled={disabled || busy || !value.trim()}
          onMouseDown={(e) => e.preventDefault()}
          className="h-[44px] rounded-xl bg-tgbutton px-5 text-[15px] font-bold text-tgbuttontext active:brightness-90 disabled:opacity-35"
        >
          {busy ? "…" : "→"}
        </button>
      </form>
      {suggestions.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2">
          {suggestions.map((n) => (
            <button
              key={n.uk}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => submit(n.uk)}
              className="rounded-full bg-[rgba(128,128,128,0.18)] px-3 py-1.5 text-sm active:opacity-70"
            >
              {n.uk}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
